/**
 * Scale Degree Drill — free-practice mode.
 *
 * Generates drill rounds from a DrillConfig. Not FSRS-driven by default;
 * callers can opt in by calling applyReview with drillItemId() after each round.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  appendCadence,
  pickKey,
  normalizeMode,
  MODE_DISPLAY_NAMES,
  type CadenceOptions,
  type KeyChoice,
  type ModeOrAlias,
} from "./utils.js";
import { buildResolutionPlan } from "./generators/scale-degree.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExerciseScaleType =
  | "major"
  | "natural-minor"
  | "harmonic-minor"
  | "melodic-minor"
  | "dorian"
  | "phrygian"
  | "lydian"
  | "mixolydian"
  | "locrian"
  | "phrygian-dominant"
  | "lydian-dominant"
  | "major-pentatonic"
  | "minor-pentatonic"
  | "blues"
  | "whole-tone";

export interface DrillConfig {
  scaleType: ExerciseScaleType;
  /** "fixed" = always fixedTonicMidi; "random" = random from common keys. */
  tonic: "fixed" | "random";
  /** MIDI note for the fixed tonic. Only used when tonic === "fixed". */
  fixedTonicMidi: number;
  /** Which part of the scale to draw notes from. Ignored for pentatonic types. */
  range: "lower" | "upper" | "full";
  /** How many octaves to span. Only relevant when range === "full" and scale is not pentatonic. */
  octaves: 1 | 2;
  /** Whether the app reveals answers immediately (listening) or waits for user input (graded). */
  drillMode: "listening" | "graded";
  /** When true, calls to applyReview after each round update FSRS card weights. */
  fsrsEnabled: boolean;
}

export const DEFAULT_DRILL_CONFIG: DrillConfig = {
  scaleType: "major",
  tonic: "fixed",
  fixedTonicMidi: 60,
  range: "full",
  octaves: 1,
  drillMode: "graded",
  fsrsEnabled: false,
};

export interface DrillRound {
  audioPlan: AudioPlan;
  /** 1–N where N is the number of scale degrees. */
  correctDegree: number;
  correctSyllable: string;
  /** All syllables for this scale type, for the answer surface. */
  syllables: readonly string[];
  tonicMidi: number;
  keyName: string;
  /** Stepwise resolution walk to the nearest tonic — played after a wrong answer. */
  teachAudio?: AudioPlan;
}

// ─── Scale type spec ──────────────────────────────────────────────────────────

interface ScaleSpec {
  /** Mode used for cadence building and random key selection. */
  mode: ModeOrAlias;
  /** Semitone offsets from the tonic for each scale degree. */
  pattern: readonly number[];
  /** Movable-do solfège syllables (one per degree). */
  solfege: readonly string[];
  /** Human-readable name. */
  displayName: string;
  /** Default fixed-tonic MIDI when the user picks a specific key. */
  defaultTonicMidi: number;
  /** True for fixed-range scales (pentatonic, blues, whole-tone) — range/octave settings do not apply. */
  isPentatonic: boolean;
}

export const SCALE_SPECS: Record<ExerciseScaleType, ScaleSpec> = {
  "major": {
    mode: "major",
    pattern: [0, 2, 4, 5, 7, 9, 11],
    solfege: ["do", "re", "mi", "fa", "sol", "la", "ti"],
    displayName: "Major",
    defaultTonicMidi: 60,
    isPentatonic: false,
  },
  "natural-minor": {
    mode: "minor",
    pattern: [0, 2, 3, 5, 7, 8, 10],
    solfege: ["do", "re", "me", "fa", "sol", "le", "te"],
    displayName: "Natural minor",
    defaultTonicMidi: 69,
    isPentatonic: false,
  },
  "harmonic-minor": {
    mode: "minor",           // cadence uses natural-minor chords
    pattern: [0, 2, 3, 5, 7, 8, 11],
    solfege: ["do", "re", "me", "fa", "sol", "le", "ti"],
    displayName: "Harmonic minor",
    defaultTonicMidi: 69,
    isPentatonic: false,
  },
  "melodic-minor": {
    mode: "minor",           // cadence uses natural-minor chords
    pattern: [0, 2, 3, 5, 7, 9, 11],
    solfege: ["do", "re", "me", "fa", "sol", "la", "ti"],
    displayName: "Melodic minor",
    defaultTonicMidi: 69,
    isPentatonic: false,
  },
  "dorian": {
    mode: "dorian",
    pattern: [0, 2, 3, 5, 7, 9, 10],
    solfege: ["do", "re", "me", "fa", "sol", "la", "te"],
    displayName: "Dorian",
    defaultTonicMidi: 62,    // D — canonical white-key dorian
    isPentatonic: false,
  },
  "phrygian": {
    mode: "phrygian",
    pattern: [0, 1, 3, 5, 7, 8, 10],
    solfege: ["do", "ra", "me", "fa", "sol", "le", "te"],
    displayName: "Phrygian",
    defaultTonicMidi: 64,    // E — canonical white-key phrygian
    isPentatonic: false,
  },
  "lydian": {
    mode: "lydian",
    pattern: [0, 2, 4, 6, 7, 9, 11],
    solfege: ["do", "re", "mi", "fi", "sol", "la", "ti"],
    displayName: "Lydian",
    defaultTonicMidi: 65,    // F — canonical white-key lydian
    isPentatonic: false,
  },
  "mixolydian": {
    mode: "mixolydian",
    pattern: [0, 2, 4, 5, 7, 9, 10],
    solfege: ["do", "re", "mi", "fa", "sol", "la", "te"],
    displayName: "Mixolydian",
    defaultTonicMidi: 67,    // G — canonical white-key mixolydian
    isPentatonic: false,
  },
  "locrian": {
    mode: "locrian",
    pattern: [0, 1, 3, 5, 6, 8, 10],
    solfege: ["do", "ra", "me", "fa", "se", "le", "te"],
    displayName: "Locrian",
    defaultTonicMidi: 71,    // B — canonical white-key locrian
    isPentatonic: false,
  },
  "major-pentatonic": {
    mode: "major",
    pattern: [0, 2, 4, 7, 9],
    solfege: ["do", "re", "mi", "sol", "la"],
    displayName: "Major pentatonic",
    defaultTonicMidi: 60,
    isPentatonic: true,
  },
  "minor-pentatonic": {
    mode: "minor",
    pattern: [0, 3, 5, 7, 10],
    solfege: ["do", "me", "fa", "sol", "te"],
    displayName: "Minor pentatonic",
    defaultTonicMidi: 69,
    isPentatonic: true,
  },
  "phrygian-dominant": {
    mode: "phrygian",
    pattern: [0, 1, 4, 5, 7, 8, 10],
    solfege: ["do", "ra", "mi", "fa", "sol", "le", "te"],
    displayName: "Phrygian dominant",
    defaultTonicMidi: 64,    // E — canonical white-key phrygian-dom (E Harm. minor mode 5)
    isPentatonic: false,
  },
  "lydian-dominant": {
    mode: "mixolydian",
    pattern: [0, 2, 4, 6, 7, 9, 10],
    solfege: ["do", "re", "mi", "fi", "sol", "la", "te"],
    displayName: "Lydian dominant",
    defaultTonicMidi: 67,    // G — over G7 is the canonical use (melodic minor mode 4)
    isPentatonic: false,
  },
  "blues": {
    mode: "minor",
    pattern: [0, 3, 5, 6, 7, 10],
    solfege: ["do", "me", "fa", "se", "sol", "te"],
    displayName: "Blues",
    defaultTonicMidi: 60,
    isPentatonic: true,      // fixed-range: all 6 degrees, no lower/upper split
  },
  "whole-tone": {
    mode: "major",
    pattern: [0, 2, 4, 6, 8, 10],
    solfege: ["do", "re", "mi", "fi", "si", "te"],
    displayName: "Whole tone",
    defaultTonicMidi: 60,
    isPentatonic: true,      // fixed-range: all 6 degrees, no lower/upper split
  },
};

// ─── Degree pool ──────────────────────────────────────────────────────────────

/**
 * Degrees available for a given range and scale type.
 * Fixed-range scales (pentatonic, blues, whole-tone) always use all n degrees.
 * 7-note scales support lower [1–4], upper [5–8], or full [1–7].
 * Degree 8 = octave tonic for 7-note full-range: plays an octave higher,
 * answers as degree 1 ("do").
 */
function degreesForRange(range: DrillConfig["range"], spec: ScaleSpec): readonly number[] {
  if (spec.isPentatonic) {
    return Array.from({ length: spec.pattern.length }, (_, i) => i + 1);
  }
  switch (range) {
    case "lower": return [1, 2, 3, 4];
    case "upper": return [5, 6, 7, 8];
    case "full":  return [1, 2, 3, 4, 5, 6, 7];
  }
}

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

/**
 * Curriculum item ID for a given scale type + degree. The two diatonic types
 * map to existing curriculum nodes; the rest use a generic drill namespace.
 */
export function drillItemId(scaleType: ExerciseScaleType, degree: number): string {
  if (scaleType === "major") return `diatonic-degrees-major::${Math.min(degree, 7)}`;
  if (scaleType === "natural-minor") return `diatonic-degrees-minor::${Math.min(degree, 7)}`;
  return `drill-${scaleType}::${degree}`;
}

/**
 * Stable key for the DrillStatsRecord for a given config.
 * Normalises away fields that don't affect the degree pool:
 *   - pentatonics ignore range and octaves
 *   - non-full ranges ignore octaves
 */
export function drillStatsKey(
  config: Pick<DrillConfig, "scaleType" | "tonic" | "fixedTonicMidi" | "range" | "octaves">,
): string {
  const spec = SCALE_SPECS[config.scaleType];
  const tonic = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  if (spec.isPentatonic) return `${config.scaleType}|${tonic}`;
  if (config.range !== "full") return `${config.scaleType}|${tonic}|${config.range}`;
  return `${config.scaleType}|${tonic}|full|${config.octaves}`;
}

// ─── Fixed tonic helper ───────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedKey(tonicMidi: number, mode: ModeOrAlias): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const canonical = normalizeMode(mode);
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode: canonical,
    tonicMidi,
    name: `${letter}${accSym} ${MODE_DISPLAY_NAMES[canonical]}`,
  };
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildDrillRound(config: DrillConfig, seed: number, cadenceOpts?: CadenceOptions): DrillRound {
  const rng = new Rng(seed);
  const spec = SCALE_SPECS[config.scaleType];

  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedKey(config.fixedTonicMidi, spec.mode)
      : pickKey(rng, spec.mode);

  const pool = degreesForRange(config.range, spec);
  const degree = rng.pick(pool as number[]);

  // Degree 8 = octave tonic (7-note full range only).
  const isOctaveTonic = !spec.isPentatonic && degree === 8;
  const effectiveDegree = isOctaveTonic ? 1 : degree;

  // Octave shift: octave-tonic forces +12; 2-octave full-range randomises.
  let octaveShift = 0;
  if (isOctaveTonic) {
    octaveShift = 12;
  } else if (!spec.isPentatonic && config.octaves === 2 && config.range === "full") {
    octaveShift = rng.int(2) * 12;
  }

  const targetSemitone = spec.pattern[effectiveDegree - 1]!;
  const targetMidi = key.tonicMidi + 12 + octaveShift + targetSemitone;

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(targetMidi, 1.4, 0.22, "target");
  builder.advance(1.5);

  return {
    audioPlan: builder.build(),
    correctDegree: effectiveDegree,
    correctSyllable: spec.solfege[effectiveDegree - 1]!,
    syllables: spec.solfege,
    tonicMidi: key.tonicMidi,
    keyName: key.name,
    teachAudio: buildResolutionPlan(key, effectiveDegree, spec.pattern, octaveShift),
  };
}
